import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Typography,
  Box,
  IconButton,
} from '@mui/material';
import {
  Warning as WarningIcon,
  Close as CloseIcon,
  CheckCircle as CheckIcon,
  Info as InfoIcon,
} from '@mui/icons-material';
import { motion } from 'framer-motion';

const MotionBox = motion(Box);

interface ConfirmDialogProps {
  open: any;
  onClose?: any;
  onCancel?: any;
  onConfirm: any;
  title?: string;
  message?: string;
  confirmText?: string;
  cancelText?: string;
  type?: string;
  confirmColor?: 'inherit' | 'primary' | 'secondary' | 'success' | 'error' | 'info' | 'warning';
}

function ConfirmDialog({
  open,
  onClose,
  onCancel,
  onConfirm,
  title = 'Confirm Action',
  message = 'Are you sure you want to perform this action?',
  confirmText = 'Confirm',
  cancelText = 'Cancel',
  type = 'warning',
  confirmColor = 'primary',
}: ConfirmDialogProps) {
  const handleClose = onClose || onCancel;
  const getIcon = () => {
    switch (type) {
      case 'danger':
        return <WarningIcon sx={{ fontSize: 60, color: 'error.main' }} />;
      case 'success':
        return <CheckIcon sx={{ fontSize: 60, color: 'success.main' }} />;
      case 'info':
        return <InfoIcon sx={{ fontSize: 60, color: 'info.main' }} />;
      default:
        return <WarningIcon sx={{ fontSize: 60, color: 'warning.main' }} />;
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      maxWidth="xs"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 3,
          overflow: 'visible',
        },
      }}
    >
      <IconButton
        onClick={handleClose}
        sx={{
          position: 'absolute',
          right: 8,
          top: 8,
          color: 'grey.500',
        }}
      >
        <CloseIcon />
      </IconButton>

      <DialogContent sx={{ textAlign: 'center', pt: 5, pb: 3 }}>
        <MotionBox
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 200, damping: 15 }}
        >
          {getIcon()}
        </MotionBox>

        <Typography variant="h5" sx={{ mt: 2, mb: 1, fontWeight: 700 }}>
          {title}
        </Typography>

        <Typography variant="body1" color="text.secondary" sx={{ mb: 2 }}>
          {message}
        </Typography>
      </DialogContent>

      <DialogActions sx={{ px: 3, pb: 3, gap: 1 }}>
        <Button
          onClick={handleClose}
          variant="outlined"
          fullWidth
          sx={{
            borderRadius: 2,
            py: 1.2,
            fontWeight: 600,
            borderWidth: 2,
            '&:hover': {
              borderWidth: 2,
            },
          }}
        >
          {cancelText}
        </Button>
        <Button
          onClick={() => {
            onConfirm();
            onClose();
          }}
          variant="contained"
          fullWidth
          color={type === 'danger' ? 'error' : confirmColor}
          sx={{
            borderRadius: 2,
            py: 1.2,
            fontWeight: 600,
            boxShadow: 3,
            '&:hover': {
              boxShadow: 5,
            },
          }}
        >
          {confirmText}
        </Button>
      </DialogActions>
    </Dialog>
  );
}

export default ConfirmDialog;
